import { JSX, useEffect, useMemo, useState } from "react";
import UserMetaCard from "../components/UserProfile/UserMetaCard";
import UserInfoCard from "../components/UserProfile/UserInfoCard";
import UserAddressCard from "../components/UserProfile/UserAddressCard";
import PageMeta from "../shared/PageMeta";
import PageBreadcrumb from "../shared/PageBreadCrumb";
import { useAuth } from "../context/AuthContext";
import { profileService } from "../services/profileService";

// Sovereign Emerald palette
const SE = {
  paper:         '#FAF7F0',
  raised:        'rgba(255,253,246,0.97)',
  hairline:      'rgba(11,59,46,0.14)',
  title:         '#0B3B2E',
  bottle:        '#1F5A47',
  brand:         '#339274',
  champagne:     '#C9A961',
  champagneWash: 'rgba(201,169,97,0.14)',
  champagneTile: 'rgba(201,169,97,0.20)',
  champagneRing: 'rgba(201,169,97,0.45)',
  muted:         '#7C8A82',
  ink:           '#2A1F17',
};

type StatTile = {
  label: string;
  value: string;
  hint?: string;
  icon: JSX.Element;
  variant: "green" | "champagne" | "brand" | "amber";
};

export default function UserProfiles() {
  const { user, updateUser } = useAuth();
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setRefreshing(true);
      const res = await profileService.getProfile();
      if (!cancelled && res.success && res.data) updateUser(res.data);
      if (!cancelled) setRefreshing(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const completion = useMemo(() => {
    if (!user) return 0;
    const fields = [
      user.firstName || user.name,
      user.lastName,
      user.email,
      user.phone || user.mobile,
      user.bio || user.description,
      user.profilePhoto || user.profileImage,
      user.address?.country || user.country,
      user.address?.city || user.city,
      user.address?.state || user.state,
      user.address?.postalCode || user.postalCode || user.zipCode,
      user.address?.street || user.street,
    ];
    const filled = fields.filter((v) => !!v && String(v).trim().length > 0).length;
    return Math.round((filled / fields.length) * 100);
  }, [user]);

  const stats: StatTile[] = useMemo(() => {
    const departments: string[] =
      Array.isArray(user?.roles) && user!.roles.length > 0
        ? user!.roles.map((r: any) => r.department).filter(Boolean)
        : user?.activeDepartment ? [user.activeDepartment] : [];

    const roleCount =
      Array.isArray(user?.roles) && user!.roles.length > 0
        ? user!.roles.length
        : user?.role ? 1 : 0;

    const status = user?.isActive === false ? "Inactive" : "Active";

    const memberSince = (() => {
      const raw = user?.createdAt || user?.joinedAt;
      if (!raw) return "—";
      try { return new Date(raw).toLocaleDateString(undefined, { year: "numeric", month: "short" }); }
      catch { return "—"; }
    })();

    return [
      {
        label: "Departments",
        value: String(departments.length || 0),
        hint: departments.join(" · ") || "Not assigned",
        variant: "green",
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 7l9-4 9 4-9 4-9-4z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 12l9 4 9-4M3 17l9 4 9-4" />
          </svg>
        ),
      },
      {
        label: "Active Roles",
        value: String(roleCount),
        hint: user?.role?.name || (Array.isArray(user?.roles) && user!.roles[0]?.roleId?.name) || "—",
        variant: "champagne",
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ),
      },
      {
        label: "Account Status",
        value: status,
        hint: user?.isSuperAdmin ? "Super Admin" : user?.activeDepartment ? `Active in ${user.activeDepartment}` : "—",
        variant: status === "Active" ? "brand" : "amber",
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M5 13l4 4L19 7" />
          </svg>
        ),
      },
      {
        label: "Member Since",
        value: memberSince,
        hint: user?.email || "—",
        variant: "green",
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        ),
      },
    ];
  }, [user]);

  const tileIconStyle = (variant: StatTile["variant"]): React.CSSProperties => {
    if (variant === "champagne") return { backgroundColor: SE.champagneTile, color: SE.champagne, boxShadow: `inset 0 0 0 1px ${SE.champagneRing}` };
    if (variant === "brand")     return { backgroundColor: 'rgba(51,146,116,0.12)', color: SE.brand };
    if (variant === "amber")     return { backgroundColor: 'rgba(217,119,6,0.10)', color: '#B45309' };
    return { backgroundColor: 'rgba(11,59,46,0.08)', color: SE.bottle };
  };

  const tileValueStyle = (variant: StatTile["variant"]): React.CSSProperties => {
    if (variant === "champagne") return { color: SE.champagne };
    if (variant === "brand")     return { color: SE.brand };
    if (variant === "amber")     return { color: '#B45309' };
    return { color: SE.title };
  };

  return (
    <>
      <PageMeta
        title="My Profile | Sorigin OMS"
        description="View and update your personal information, address, and account preferences."
      />
      <PageBreadcrumb pageTitle="Profile" />

      <div className="space-y-5 px-5 py-5">
        {/* Hero / identity card */}
        <UserMetaCard completion={completion} />

        {/* Quick stats */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {stats.map((s) => (
            <div
              key={s.label}
              className="relative overflow-hidden rounded-2xl p-4 transition-all duration-200 hover:-translate-y-0.5"
              style={{
                backgroundColor: SE.paper,
                border: `1px solid ${SE.hairline}`,
                boxShadow: '0 1px 4px rgba(11,59,46,0.06), 0 4px 14px rgba(11,59,46,0.04)',
              }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLElement).style.boxShadow = `0 4px 16px rgba(11,59,46,0.10), 0 0 0 1px ${SE.champagneRing}`;
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLElement).style.boxShadow = '0 1px 4px rgba(11,59,46,0.06), 0 4px 14px rgba(11,59,46,0.04)';
              }}
            >
              {/* Champagne grain overlay */}
              <div className="pointer-events-none absolute inset-0 rounded-2xl" style={{ background: 'linear-gradient(135deg, rgba(201,169,97,0.04) 0%, transparent 60%)' }} />

              <div className="relative flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-[10.5px] font-bold uppercase tracking-[0.12em]" style={{ color: SE.muted }}>
                    {s.label}
                  </p>
                  <p className="mt-1.5 text-[22px] font-bold tracking-tight leading-none" style={tileValueStyle(s.variant)}>
                    {s.value}
                  </p>
                  {s.hint && (
                    <p className="mt-1.5 truncate text-[11px] font-medium" style={{ color: SE.muted }} title={s.hint}>
                      {s.hint}
                    </p>
                  )}
                </div>
                <div
                  className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl"
                  style={tileIconStyle(s.variant)}
                >
                  {s.icon}
                </div>
              </div>

              {/* Bottom champagne hairline */}
              <div className="absolute bottom-0 left-4 right-4 h-px" style={{ background: `linear-gradient(to right, transparent, ${SE.champagneRing}, transparent)` }} />
            </div>
          ))}
        </div>

        {/* Information sections */}
        <UserInfoCard />
        <UserAddressCard />

        {refreshing && (
          <p className="text-center text-xs" style={{ color: SE.muted }}>
            Refreshing profile…
          </p>
        )}
      </div>
    </>
  );
}
